import { DropzoneFile } from 'components/Dropzone/utils';
import { Listing, ListingEnquiry, ListingStage } from './Listing';
import { Role, User } from './User';
import { ObjectValues } from 'types';
import { NudgeNotification } from './Notification';
import { Enterprise, EnterpriseBusinessUnit } from './Enterprises';
import { Industry } from './Industry';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { LeadStages } from './Lead';

export const DEAL_STAGE = {
  SELLER_LEAD: 'seller_lead',
  MANDATED: 'mandated',
  LIVE: 'live',
  UNDER_OFFER: 'under_offer',
  UNDER_CONTRACT: 'under_contract',
  SOLD: 'sold',
  COMPLETE: 'complete',
  LOST: 'lost',
  ARCHIVED: 'archived',
} as const;

export type DealStage = ObjectValues<typeof DEAL_STAGE>;

export const DEAL_OFFER_STATUS = {
  EXCLUSIVE: 'exclusive',
  NON_EXCLUSIVE: 'non_exclusive',
} as const;

export type DealOfferStatus = ObjectValues<typeof DEAL_OFFER_STATUS>;

export const DEAL_CONTRACT_STATUS = {
  UNCONDITIONAL: 'unconditional',
  CONDITIONAL: 'conditional',
} as const;

export type DealContractStatus = ObjectValues<typeof DEAL_CONTRACT_STATUS>;

export type DealRoom = {
  _id: string;

  // statuses
  stage: DealStage;
  listingStage: ListingStage;
  wantToBePrivate?: boolean; // works with listingStage prop 'Draft' (only for enterprise)
  showGetBuyersBanner?: boolean; // for get-buyers banner

  shouldCheckPulse?: boolean; // open Check Pulse drawer

  // for stage under_offer
  offerStatus: DealOfferStatus;
  // for stage under_contract
  contractStatus: DealContractStatus;
  // for stage under_offer and under_contract
  underOfferLeadId: string;
  isExternalUnderOfferLead: boolean;
  // for stage sold
  settlementDate: Date;
  settlementPrice: number;
  // for stage lost
  lostMandateDescription: string;

  isPaid?: boolean; // listing is created and paid (or skip payment)
  hasUnlockPremiumFeatures?: boolean; // for enterprise members and for brokers with specific domain (for PKF on prod)
  hasBossModeUnlockedPremiumFeatures?: boolean; // boss mode unlocked or locked premium features manually

  //DEAL CREATION WIZARD
  name: string;
  industry: Industry;
  dealOutcome: DealOutcome;
  otherDealOutcome: string;
  location: DealRoomLocation;
  priceGuide: AskingPriceSelect;
  amount: number | null;
  amountFrom: number | null;
  amountTo: number | null;
  currency: Currency;
  averageStockValue?: number;
  stockAtValue: StockAtValue;
  companyName?: string;
  companyLogo?: DropzoneFile;
  totalPackage: number;
  totalPackageGST: DealRoomTotalPackageGST;
  listingIM: DropzoneFile;
  projectedSalePrice?: number;
  successFeeShare?: number;
  successFeePercentage?: number;
  franchisorFeePercentage?: number;
  revenueShares: Partial<DealRoomRevenueShare>[];

  //BOSS-MODE PROPS
  recordHubspotId: string;
  vendorIntro?: boolean;
  inLieuListingFee?: boolean;
  buyerIntroducedByResolve?: boolean;
  brokerUpfrontFee?: number;
  resolveShareUpfrontFee?: number;
  brokerMonthlyRetainer?: number;
  resolveShareRetainer?: number;
  askingPrice?: number;
  brokerSuccessFee?: number;
  resolveShareSuccessFee?: number;
  leadBroker?: string;
  brokerFirm?: string;
  ACN?: string;
  ABN?: string;
  postcode?: string;
  brokerReferenceCode?: string;
  dealNotes?: string;
  relationshipManager?: string;
  nudgeNotification?: NudgeNotification;

  //ADDITIONAL PROPS
  colleaguesCount?: number;
  colleagues: Partial<Colleague>[];
  colleagueIds: string[];
  archivedOn?: Date | null;
  isBrokerManaged: boolean;

  leadBrokers: Colleague[];
  coBrokers: Colleague[];

  ownerName?: string;
  ownerBusinessUnit?: string;

  //for sorting
  progressedStep: number;
  watchlistCounter: number;
  publishedOn: Date;
  lastUpdated: Date;
  createdAt: Date;

  //VIRTUAL PROPS
  numberOfLeads: number;
  numberOfFiles: number;
  isColleague: boolean;
  bossModeReferrerOptions: string[];

  //RELATIONS
  listingId: string;
  listingEnquiryId: string;
  dataRoomId: string;
  userId: string;
  listing: Partial<Listing> | null;
  listingEnquiry: Partial<ListingEnquiry> | null;
  user: Partial<User> | null;

  // for seller
  hasDataRoomAccess?: boolean;

  portfolioName: string;

  enterpriseId: string;
  enterprise: Partial<Enterprise> | null;
  isRWC?: boolean;

  enterpriseEntityId: string;

  enterpriseBusinessUnitId: string;
  enterpriseBusinessUnit: Partial<EnterpriseBusinessUnit> | null;

  listingNDA?: DropzoneFile;

  enabledQualificationQuestions: boolean;
  qualificationQuestions: QualificationQuestionType[];
  shouldAskQuestions?: boolean; // should user answer questions on enquire

  // notes
  unreadNotesCount?: number;
  unrepliedNotesCount?: number;

  // the most important X number of data room nodes
  dataRoomTopNodes: DealRoomTopNodes[];

  leadStagesCopies?: LeadStagesCopies;
};

export type LeadStagesCopies = Record<LeadStages, string>;

export type DealRoomTopNodes = {
  dataRoomNodeId: string;
  dataRoomNode: DataRoomNode;
  orderNumber: number;
};

export type Colleague = {
  _id: string;
  id: string;
  email: string;
  name: string;
  isAccepted: boolean;
  isOwner: boolean;
  sendNotifications: boolean;
  showOnListing: boolean;
  useMessaging: boolean;
  dataRoomAdmin: boolean;
  role: DealRoomColleagueRole;
  userId: string;
  user: User;
  invitedAt?: Date;
  userRole?: Role;
};

export const DEAL_OUTCOME = {
  SALE_CAMPAIGN: 'sale_campaign',
  AUDIT: 'audit',
  CAPITAL_RAISE: 'capital_raise',
  COMMERCIAL_FINANCE_APPLICATION: 'commercial_finance_application',
  SECURE_DOC_STORAGE: 'secure_doc_storage',
  OTHER: 'other',
} as const;

export type DealOutcome = typeof DEAL_OUTCOME[keyof typeof DEAL_OUTCOME];

export type PublishDealRoomValues = Pick<
  DealRoom,
  | 'name'
  | 'industry'
  | 'dealOutcome'
  | 'otherDealOutcome'
  | 'location'
  | 'priceGuide'
  | 'amount'
  | 'amountFrom'
  | 'amountTo'
  | 'currency'
  | 'averageStockValue'
  | 'stockAtValue'
  | 'companyName'
  | 'companyLogo'
  | 'totalPackage'
  | 'totalPackageGST'
  | 'colleagues'
  | 'revenueShares'
  | 'projectedSalePrice'
  | 'successFeeShare'
  | 'successFeePercentage'
  | 'franchisorFeePercentage'
> & {
  createListing?: string;
  uploadListingIM?: string;
  listingIM?: DropzoneFile;
};

export type EditDealRoomValues = PublishDealRoomValues &
  Pick<DealRoom, 'stage' | 'listingStage'>;

export const DEAL_ROOM_TOTAL_PACKAGE_GST = {
  INCLUSIVE: 'inclusive',
  EXCLUSIVE: 'exclusive',
} as const;

export type DealRoomTotalPackageGST = ObjectValues<
  typeof DEAL_ROOM_TOTAL_PACKAGE_GST
>;

export const ASKING_PRICE_SELECT = {
  FIXED_PRICE: 'fixed_price',
  PRICE_RANGE: 'price_range',
  EXPRESSION_OF_INTEREST: 'expression_of_interest',
} as const;

export type AskingPriceSelect = ObjectValues<typeof ASKING_PRICE_SELECT>;

export const STOCK_AT_VALUE = {
  INCLUDE_STOCK_AT_VALUE: 'include_stock_at_value',
  EXCLUDE_STOCK_AT_VALUE: 'exclude_stock_at_value',
  NO_STOCK_AT_VALUE: 'no_stock_at_value',
} as const;

export type StockAtValue = ObjectValues<typeof STOCK_AT_VALUE>;

export const DEAL_ROOM_COLLEAGUE_ROLE = {
  LEAD_BROKER: 'lead_broker',
  CO_BROKER: 'co_broker',
  EXEC_ASSISTANT: 'exec_assistant',
  DEAL_SUPPORT: 'deal_support',
  NONE: 'none',
} as const;

export type DealRoomColleagueRole = ObjectValues<
  typeof DEAL_ROOM_COLLEAGUE_ROLE
>;

export const BASE_DEAL_ROOM_COLLEAGUE_ROLE_COPY = {
  [DEAL_ROOM_COLLEAGUE_ROLE.EXEC_ASSISTANT]: 'exec_assistant',
  [DEAL_ROOM_COLLEAGUE_ROLE.DEAL_SUPPORT]: 'deal_support',
  [DEAL_ROOM_COLLEAGUE_ROLE.NONE]: 'role',
} as const;

export const DEAL_ROOM_COLLEAGUE_ROLE_COPY = {
  ...BASE_DEAL_ROOM_COLLEAGUE_ROLE_COPY,
  [DEAL_ROOM_COLLEAGUE_ROLE.LEAD_BROKER]: 'lead_broker',
  [DEAL_ROOM_COLLEAGUE_ROLE.CO_BROKER]: 'co_broker',
} as const;

export const RWC_DEAL_ROOM_COLLEAGUE_ROLE_COPY = {
  ...BASE_DEAL_ROOM_COLLEAGUE_ROLE_COPY,
  [DEAL_ROOM_COLLEAGUE_ROLE.LEAD_BROKER]: 'agent',
  [DEAL_ROOM_COLLEAGUE_ROLE.CO_BROKER]: 'co_agent',
} as const;

export const CURRENCIES = {
  AUD: 'aud',
  NZD: 'nzd',
} as const;

export type Currency = ObjectValues<typeof CURRENCIES>;

export const AU_STATES = {
  NSW: 'NSW',
  VIC: 'VIC',
  QLD: 'QLD',
  WA: 'WA',
  SA: 'SA',
  TAS: 'TAS',
  NT: 'NT',
  ACT: 'ACT',
} as const;

export const NZ_REGIONS = {
  AUK: 'AUK',
  WGN: 'WGN',
  NSN: 'NSN',
  CAN: 'CAN',
  OTA: 'OTA',
  HKB: 'HKB',
  TKI: 'TKI',
} as const;

export const DEAL_ROOM_LOCATION = {
  ...AU_STATES,
  ...NZ_REGIONS,
} as const;

export type DealRoomLocation = ObjectValues<typeof DEAL_ROOM_LOCATION>;

export type DealRoomRevenueShare = {
  _id: string;
  description: RevenueDescription;
  percentage: number;
  orderNumber: number;
  userId: string;
  email?: string;
  label?: string;
  user: Partial<User> | null;
  dealRoomId: string;
  dealRoom: Partial<DealRoom> | null;
};

export const REVENUE_DESCRIPTION = {
  BUYER_REFERRAL: 'buyer_referral',
  SELLER_REFERRAL: 'seller_referral',
  BROKERS_COMMISSION: 'brokers_commission',
  ADD_CUSTOM: 'add_custom',
} as const;

export type RevenueDescription = ObjectValues<typeof REVENUE_DESCRIPTION>;

export const CREATE_LISTING_OPTIONS = {
  DRAFT_LISTING: 'now',
  SET_DEAL_ROOM: 'later',
} as const;

export type TotalChatCount = {
  totalUnreadChatsCount: number;
  totalUnrepliedChatsCount: number;
};

export type DataRoomDashboardStats = {
  folders: number;
  files: number;
  faqs: number;
  questions: number;
};

export const SCORE = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  NO_SCORE: 'no-score',
} as const;

export type Score = ObjectValues<typeof SCORE>;

export type QualificationQuestionAnswer = {
  id?: string; // for reordering
  autoFocus?: boolean; // autoFocus if new

  answer: string;
  score: Score;
  order?: number;
};

export type QualificationQuestionType = {
  id?: string; // for reordering
  autoFocus?: boolean; // autoFocus if new

  title: string;
  answers: QualificationQuestionAnswer[];
  required?: boolean;
  order?: number;
};

export type TransferResponse = {
  colleague: Colleague; // new owner
  deal: DealRoom;
};

export type PreviousDeal = {
  leadId: string;
  dealRoomId: string;
  dealName: string;
};

export type BuyerEnquiryHistory = {
  numberOfPreviousEnquiries: number;
  lastEnquire: Date;
  previousDeals: PreviousDeal[];

  existLead: boolean;

  buyerId: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerEmail: string;
};
